import React, { useState, useMemo } from 'react'
import useQueryParams from '../../hooks/useQueryParams'
import { useParams, useLocation, useHistory } from 'react-router-dom'
import { useQuery } from 'react-query'
import QueryKeys from '../../utils/constants/queryKeys'
import LoaderDots from '../../components/loaders/LoaderDots/LoaderDots'
import getSingleTeam from '../../Apis/team/getSingleTeam'
import getSeasonTeams from '../../Apis/season/getSeasonTeams'
import getSeasonsByTeam from '../../Apis/season/getSeasonsByTeam'
import { useTranslate } from 'react-admin'
import teamStatisticsPageStyles from './teamStatisticsPage.styles'
import PlayersTable from './PlayersTable'
import COLORS from '../../utils/constants/colors'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import TuneIcon from '@material-ui/icons/Tune'
import NoStatsAvailable from './NoStatsAvalaiblePage'
import {
  makeStyles,
  Card,
  CardContent,
  Divider,
  Typography,
  CardMedia,
  IconButton,
  TableContainer,
  Paper,
  MenuItem,
  Select,
  Box,
  Container,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
} from '@material-ui/core'
import MyToggleButton from '../../components/MyToggleButton/MyToggleButton'
import StatsInfoDialog from './StatsInfoDialog'
import StatsNavigationPage from './StatsNavigationPage'
import ContainerPadding from '../../components/containerPadding/containerPadding'
import getTeamStats from '../../Apis/stats/getTeamStats'
import getFederationSeasonTeamStats from '../../Apis/stats/getFederationSeasonTeamStats'
import statsInfo from '../../utils/constants/statsInfo'
import DefaultOrgIcon from '../../assets/icons/DefaultOrgIcon'
import StatsGrid from './StatsGrid'
import { useCmsScope } from '../../context/useCmsScope'
import ORGANISATION_TYPES from '../../utils/constants/OrganisationTypes'


const TeamStatisticsPage = () => {
  const useStyles = makeStyles(teamStatisticsPageStyles)
  const [toggleValue, setToggleValue] = useState('average')
  const [open, setOpen] = useState(false)
  const [filterOpen, setFilterOpen] = useState(false)
  const [pendingSeasonId, setPendingSeasonId] = useState(null)
  const [pendingStageId, setPendingStageId] = useState(null)
  const [appliedSeasonId, setAppliedSeasonId] = useState(null)
  const [appliedStageId, setAppliedStageId] = useState(null)
  const translate = useTranslate()
  const classes = useStyles()
  const queryParams = useQueryParams()
  const { teamId } = useParams()
  const location = useLocation()
  const history = useHistory()

  const isSeasonTeamRoute = location.pathname.includes('/seasonTeams')
  const { orgId, orgType } = useCmsScope()
  const isFederation = orgType === ORGANISATION_TYPES.ORG_FEDERATION
  const useFederationShape = isSeasonTeamRoute && isFederation

  const activeTeamId = teamId || queryParams?.team

  const handleGoBack = () => {
    history.goBack()
  }

  const { data: teamData, isFetching: isTeamFetching } = useQuery(
    [QueryKeys.GET_SINGLE_TEAM, orgId, activeTeamId, isSeasonTeamRoute],
    () => {
      if (isSeasonTeamRoute) {
        return getSeasonTeams(orgId, null, null, activeTeamId)
      }
      // The scope's org: the selected one, or the paired federation the page shows.
      return getSingleTeam(orgId, activeTeamId)
    },
    {
      enabled: Boolean(activeTeamId) && Boolean(orgId),
    },
  )

  const seasonTeamsList = isSeasonTeamRoute && Array.isArray(teamData) ? teamData : null

  // A team's season-teams all share the same teamId, so derive it from the first entry.
  // Keeping this independent of the selected season avoids a dependency cycle with the
  // seasons query below, whose result feeds effectiveSeasonId (used to pick the season-team).
  const resolvedTeamId = useFederationShape
    ? (seasonTeamsList?.[0]?.teamId ?? null)
    : activeTeamId

  // For federation: fetch seasons (with embedded stages) keyed by the actual team ID
  const { data: seasonsData, isFetching: isSeasonsFetching } = useQuery(
    [QueryKeys.GET_SEASONS_BY_TEAM, orgId, resolvedTeamId],
    () => getSeasonsByTeam({ orgId, teamId: resolvedTeamId }),
    { enabled: useFederationShape && Boolean(orgId) && Boolean(resolvedTeamId) },
  )
  const seasons = seasonsData || []

  // Initial season comes from ?seasonId= (set when arriving from the CMS by clicking a
  // team), falling back to the most recent. Derived rather than effect-driven so the
  // stats query fires as soon as seasons load — avoiding an extra render and a loader
  // flash. The filter dialog overrides this by setting appliedSeasonId (takes precedence).
  const preferredSeasonId = useMemo(() => {
    if (seasons.length === 0) return null
    const fromQuery = queryParams.seasonId && seasons.find((s) => s.id === queryParams.seasonId)
    return fromQuery ? fromQuery.id : seasons[0].id
  }, [seasons, queryParams.seasonId])
  const effectiveSeasonId = appliedSeasonId ?? preferredSeasonId

  // Roster (Players tab) and games must be scoped to the season-team for the SELECTED
  // season, not always the first one — otherwise the filter dropdown only re-queries
  // stats while the roster stays pinned to seasonTeamsList[0]. Fall back to the first
  // season-team until a season is resolved (and for the non-federation season-team route,
  // where the filter is hidden and effectiveSeasonId stays null).
  const team = isSeasonTeamRoute
    ? (seasonTeamsList?.find((st) => st.seasonId === effectiveSeasonId) ?? seasonTeamsList?.[0] ?? null)
    : teamData
  const resolvedSeasonTeamId = isSeasonTeamRoute ? team?.id : activeTeamId

  // Stages for the season currently selected inside the modal (already embedded in season)
  const pendingSeason = seasons.find(s => s.id === pendingSeasonId)
  const stages = pendingSeason?.stages || []

  const { data: stats, isFetching } = useQuery(
    [QueryKeys.GET_TEAM_STATS, resolvedTeamId, resolvedSeasonTeamId, useFederationShape, effectiveSeasonId, appliedStageId],
    () => {
      if (useFederationShape) {
        return getFederationSeasonTeamStats({
          orgId: orgId,
          teamId: resolvedTeamId,
          seasonId: effectiveSeasonId,
          stageId: appliedStageId,
        })
      }
      return getTeamStats({ teamId: activeTeamId })
    },
    {
      enabled: useFederationShape
        ? Boolean(resolvedTeamId) && Boolean(effectiveSeasonId)
        : Boolean(isSeasonTeamRoute ? resolvedSeasonTeamId : activeTeamId),
    },
  )

  const teamWins = useFederationShape
    ? (stats?.data?.wins || 0)
    : (stats?.data?.teamStats?.wins || 0)

  const teamLosses = useFederationShape
    ? (stats?.data?.losses || 0)
    : ((stats?.data?.teamStats?.gameCount || 0) - (stats?.data?.teamStats?.wins || 0))

  const teamStatsForGrid = useFederationShape
    ? (() => {
        const t = stats?.data?.teamTotals || {}
        const a = stats?.data?.teamAverages || {}
        return {
          avgPoints: a.points,
          avgRebounds: a.totalRebounds,
          avgAssists: a.assists,
          avgSteals: a.steals,
          avgBlocksAgainst: a.blocks,
          avgPir: a.pir,
          points: t.points,
          rebounds: t.totalRebounds,
          assists: t.assists,
          steals: t.steals,
          blocksAgainst: t.blocks,
          pir: t.pir,
        }
      })()
    : stats?.data?.teamStats

  const playerStatsForTable = useFederationShape
    ? (stats?.data?.players || []).map(p => ({
        playerId: p.playerId,
        fullName: p.fullName,
        stats: {
          gamesPlayed: p.totals?.gamesPlayed,
          gamesStarted: p.totals?.gamesStarted,
          points: p.totals?.points,
          made2Pointers: p.totals?.made2Pointers,
          made3Pointers: p.totals?.made3Pointers,
          made1Pointers: p.totals?.made1Pointers,
          offensiveRebounds: p.totals?.offensiveRebounds,
          defensiveRebounds: p.totals?.defensiveRebounds,
          rebounds: p.totals?.totalRebounds,
          assists: p.totals?.assists,
          steals: p.totals?.steals,
          turnovers: p.totals?.turnovers,
          blocksAgainst: p.totals?.blocksAgainst,
          fouls: p.totals?.fouls,
          foulsDrawn: p.totals?.foulsDrawn,
          pir: p.totals?.pir,
          avgPoints: p.averages?.points,
          avgMade2Pointers: p.averages?.made2Pointers,
          avgMade3Pointers: p.averages?.made3Pointers,
          avgMade1Pointers: p.averages?.made1Pointers,
          avgOffensiveRebounds: p.averages?.offensiveRebounds,
          avgDefensiveRebounds: p.averages?.defensiveRebounds,
          avgRebounds: p.averages?.totalRebounds,
          avgAssists: p.averages?.assists,
          avgSteals: p.averages?.steals,
          avgTurnovers: p.averages?.turnovers,
          avgBlocksAgainst: p.averages?.blocksAgainst,
          avgFouls: p.averages?.fouls,
          avgFoulsDrawn: p.averages?.foulsDrawn,
          avgPir: p.averages?.pir,
        },
      }))
    : (stats?.data?.playerStats || [])

  const appliedSeason = seasons.find(s => s.id === effectiveSeasonId)
  const appliedSeasonName = appliedSeason?.name
  const appliedStageName = appliedStageId
    ? (appliedSeason?.stages || []).find(s => s.id === appliedStageId)?.name
    : null
  const filterLabel = [
    appliedStageName ?? 'All phases',
    appliedSeasonName,
  ].filter(Boolean).join(', ')

  const handleOpenFilter = () => {
    setPendingSeasonId(effectiveSeasonId)
    setPendingStageId(appliedStageId)
    setFilterOpen(true)
  }

  const handleApplyFilter = () => {
    setAppliedSeasonId(pendingSeasonId)
    setAppliedStageId(pendingStageId)
    setFilterOpen(false)
  }

  const handleClickOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  return (
    <Container>
      {isSeasonTeamRoute && (
        <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2, marginTop: 2 }}>
          <IconButton onClick={handleGoBack} aria-label="go back">
            <ArrowBackIcon style={{ fill: COLORS.orange }} />
          </IconButton>
          <Typography variant="h6" style={{ marginLeft: 8 }}>
            Team Statistics
          </Typography>
        </Box>
      )}
      {isFetching || isTeamFetching || isSeasonsFetching ? (
        <ContainerPadding paddingValue='45%'>
          <LoaderDots
            style={{
              fill: COLORS.orange,
              width: 70,
              height: 70,
            }}
          />
        </ContainerPadding>
      ) : (
        <ContainerPadding paddingValue='2%' className={classes.statsContainer}>
          <Card className={classes.teamCard}>
            {team?.teamLogoUrl || team?.teamLogo ? (
              <CardMedia className={classes.teamLogo} image={team.teamLogoUrl || team.teamLogo} />
            ) : (
              <DefaultOrgIcon className={classes.teamLogo} />
            )}
            <CardContent className={classes.teamCardContent} style={{ flex: 1 }}>
              <Box className={classes.infoBox}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant='h5' className={classes.teamName}>
                    {team?.name}
                  </Typography>
                  {useFederationShape && seasons.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Typography variant='body2' color='textSecondary'>
                        {filterLabel}
                      </Typography>
                      <IconButton size='small' onClick={handleOpenFilter}>
                        <TuneIcon style={{ fill: COLORS.orange }} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100px' }}>
                  <Table
                    sx={{
                      width: '100%',
                      tableLayout: 'fixed',
                      '& td, & th': {
                        padding: '0px 0px',
                        borderBottom: 'none',
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow style={{ backgroundColor: 'transparent' }}>
                        <TableCell
                          style={{
                            width: '50%',
                            backgroundColor: 'transparent',
                            borderColor: 'transparent',
                            textAlign: 'center',
                            borderRight: '1px solid black',
                            borderBottom: 'none',
                            padding: '0px 10px',
                            margin: '0px 0px',
                          }}
                        >
                          <Typography className={classes.teamVictories}>
                            {translate('ra.text.won')}
                          </Typography>
                        </TableCell>
                        <TableCell
                          style={{
                            width: '50%',
                            backgroundColor: 'transparent',
                            borderColor: 'transparent',
                            textAlign: 'center',
                            borderBottom: 'none',
                            padding: '0px 0px',
                            margin: '0px 0px',
                          }}
                        >
                          <Typography className={classes.teamVictories}>
                            {translate('ra.text.lost')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell
                          style={{
                            backgroundColor: 'transparent',
                            borderColor: 'transparent',
                            textAlign: 'center',
                            borderRight: '1px solid black',
                            padding: '0px 10px',
                            margin: '0px 0px',
                          }}
                        >
                          <Typography sx={{ fontSize: '3rem', fontWeight: 'bold' }}>
                            {teamWins}
                          </Typography>
                        </TableCell>
                        <TableCell
                          style={{
                            backgroundColor: 'transparent',
                            borderColor: 'transparent',
                            textAlign: 'center',
                            padding: '0px 10px',
                            margin: '0px 0px',
                          }}
                        >
                          <Typography sx={{ fontSize: '3rem', fontWeight: 'bold' }}>
                            {teamLosses}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {!resolvedSeasonTeamId ? (
            <Box sx={{ textAlign: 'center', padding: '40px 20px' }}>
              <ErrorOutlineIcon style={{ fontSize: 48, color: COLORS.orange, marginBottom: 16 }} />
              <Typography variant='h6' gutterBottom>
                {translate('ra.text.teamNotInSeason', { _: 'This team is not part of any season' })}
              </Typography>
              <Typography variant='body1' color='textSecondary'>
                {translate('ra.text.addTeamToSeasonPrompt', { _: 'Add this team to a season through the CMS page to view statistics.' })}
              </Typography>
            </Box>
          ) : (
            <StatsNavigationPage resolvedSeasonTeamId={resolvedSeasonTeamId} resolvedTeamId={resolvedTeamId} resolvedSeasonId={team?.seasonId} orgId={orgId}>
              {!stats?.data ? (
                <NoStatsAvailable />
              ) : (
                <>
                  <div className={classes.tabMenu}>
                    <MyToggleButton
                      left={{
                        value: 'average',
                        label: translate('ra.buttons.average'),
                      }}
                      right={{
                        value: 'accumulate',
                        label: translate('ra.buttons.accumulate'),
                      }}
                      setToggleValue={setToggleValue}
                      toggleValue={toggleValue}
                    />
                  </div>
                  <Divider variant='middle' style={{ margin: '15px 0' }} />
                  <StatsGrid
                    stats={teamStatsForGrid}
                    showValue={toggleValue}
                    displayStats={useFederationShape ? stats?.data?.displayStats : stats?.data?.teamStats?.displayStats}
                  />
                  <Divider variant='middle' style={{ margin: '15px 0' }} />
                  <div>
                    <span>{translate('ra.text.selectedPlayers')}</span>
                    <IconButton onClick={handleClickOpen}>
                      <ErrorOutlineIcon />
                    </IconButton>
                  </div>
                  <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                    <PlayersTable
                      allPlayerStats={playerStatsForTable}
                      showValue={toggleValue}
                      displayStats={useFederationShape ? stats?.data?.displayStats : stats?.data?.teamStats?.displayStats}
                    />
                  </TableContainer>

                  <StatsInfoDialog open={open} onClose={handleClose} statsInfo={statsInfo} />
                </>
              )}
            </StatsNavigationPage>
          )}
        </ContainerPadding>
      )}

      {/* Filter modal — federation only */}
      <Dialog open={filterOpen} onClose={() => setFilterOpen(false)} fullWidth maxWidth='xs'>
        <DialogTitle>Filter table</DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth style={{ marginBottom: 24 }}>
            <InputLabel id='filter-season-label'>Season</InputLabel>
            <Select
              labelId='filter-season-label'
              value={pendingSeasonId || ''}
              onChange={(e) => {
                setPendingSeasonId(e.target.value)
                setPendingStageId(null)
              }}
            >
              {seasons.map(s => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id='filter-stage-label'>Phase</InputLabel>
            <Select
              labelId='filter-stage-label'
              value={pendingStageId || ''}
              onChange={(e) => setPendingStageId(e.target.value || null)}
            >
              <MenuItem value=''>All phases</MenuItem>
              {stages.map(s => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleApplyFilter}
            variant='outlined'
            style={{ borderColor: COLORS.orange, color: COLORS.orange, borderRadius: 20 }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default TeamStatisticsPage
